import { LitElement, html, css } from 'lit'
import { StoreMixin, store } from '../store.js'
import { api } from '../api.js'
import { toast } from './app-toast.js'

// Agent admin (§7.11, §19.4). Agent-only (route guard). Suspend blocks sign-in,
// revokes sessions, and reassigns the agent's open tickets to another agent or a
// group queue; reactivate restores access. Each agent also carries their Workamajig
// user key — without one, tasks created for them land on the job unassigned.
class PageAgents extends StoreMixin(LitElement) {
  static properties = {
    _agents: { state: true },
    _groups: { state: true },
    _loading: { state: true },
    _suspending: { state: true },   // id of the agent whose reassignment form is open
    _target: { state: true },       // '' (unassign) | 'agent:<id>' | 'group:<id>'
    _editingWmj: { state: true },   // id of the agent whose Workamajig key is being edited
    _wmjKey: { state: true }
  }

  static styles = css`
    :host { display: block; }
    h1 { color: var(--bx-brand); margin: 0 0 0.25rem; font-size: 1.4rem; }
    p.lede { color: var(--bx-muted); margin: 0 0 1rem; }
    .list { background: var(--bx-surface); border: 1px solid var(--bx-border); border-radius: var(--bx-radius);
            box-shadow: var(--bx-shadow); overflow: hidden; }
    .row { padding: 0.85rem 1rem; border-bottom: 1px solid var(--bx-border); }
    .row:last-child { border-bottom: none; }
    .head { display: flex; align-items: center; gap: 1rem; }
    .name { font-weight: 600; color: var(--bx-ink); }
    /* Group membership (§3.15) — which queues this agent answers for, at a glance.
       Takes the flexible space so the badges and actions stay right-aligned. */
    .groups { flex: 1; display: flex; flex-wrap: wrap; gap: 0.3rem; align-items: center; }
    .group-tag { font-size: 0.72rem; padding: 0.1rem 0.5rem; border-radius: 999px;
                 background: var(--bx-bg); color: var(--bx-muted); border: 1px solid var(--bx-border);
                 white-space: nowrap; }
    .group-tag.none { border-style: dashed; }
    .badge { font-size: 0.72rem; padding: 0.1rem 0.5rem; border-radius: 999px; }
    .badge.active { background: #dcfce7; color: #166534; }
    .badge.suspended { background: #fee2e2; color: #991b1b; }
    .count { color: var(--bx-muted); font-size: 0.82rem; }
    button { font: inherit; cursor: pointer; padding: 0.35rem 0.7rem; border: 1px solid var(--bx-border);
             border-radius: var(--bx-radius); background: var(--bx-surface); color: var(--bx-ink); font-size: 0.82rem; }
    button.danger { border-color: #dc2626; color: #dc2626; }
    button.primary { background: var(--bx-accent); color: var(--bx-accent-ink); border-color: transparent; font-weight: 700; }
    .reassign { margin-top: 0.75rem; display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }
    select { font: inherit; padding: 0.4rem 0.5rem; border: 1px solid var(--bx-border); border-radius: var(--bx-radius);
             background: var(--bx-bg); color: var(--bx-ink); }
    .loading { padding: 3rem; text-align: center; color: var(--bx-muted); }
    /* Workamajig identity (§19.4) — muted, but loud when it's missing. */
    .wmj { margin-top: 0.5rem; display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap;
           font-size: 0.82rem; color: var(--bx-muted); }
    .wmj .missing { color: #b45309; }
    .wmj input { font: inherit; padding: 0.35rem 0.5rem; border: 1px solid var(--bx-border);
                 border-radius: var(--bx-radius); background: var(--bx-bg); color: var(--bx-ink); width: 12rem; }
  `

  constructor() {
    super()
    this._agents = []
    this._groups = []
    this._loading = true
    this._suspending = null
    this._target = ''
    this._editingWmj = null
    this._wmjKey = ''
  }

  connectedCallback() {
    super.connectedCallback()
    this._load()
  }

  async _load() {
    this._loading = true
    try {
      const [{ agents }, { groups }] = await Promise.all([api.get('/users/agents'), api.get('/groups')])
      this._agents = agents
      this._groups = groups
    } catch (err) {
      toast(err.message || 'Failed to load agents', 'error')
    } finally {
      this._loading = false
    }
  }

  _openSuspend(id) { this._suspending = id; this._target = '' }

  async _confirmSuspend(agent) {
    const body = {}
    if (this._target.startsWith('agent:')) body.reassignToAgentId = this._target.slice(6)
    else if (this._target.startsWith('group:')) body.reassignToGroupId = this._target.slice(6)
    try {
      const res = await api.post(`/users/${agent.id}/suspend`, body)
      toast(`Suspended ${agent.fullName || agent.email} · reassigned ${res.reassignedCount} ticket${res.reassignedCount === 1 ? '' : 's'}`, 'success')
      this._suspending = null
      this._load()
    } catch (err) {
      toast(err.message || 'Failed to suspend', 'error')
    }
  }

  _openWmj(agent) {
    this._editingWmj = agent.id
    this._wmjKey = agent.workamajigUserId || ''
  }

  async _saveWmj(agent) {
    try {
      await api.patch(`/users/${agent.id}/workamajig`, { workamajigUserId: this._wmjKey.trim() || null })
      toast(this._wmjKey.trim() ? 'Workamajig user set' : 'Workamajig user cleared', 'success')
      this._editingWmj = null
      this._load()
    } catch (err) {
      toast(err.message || 'Failed to save the Workamajig user', 'error')
    }
  }

  // What §19.4 resolves for this agent right now: the override, else the email match
  // in the cached staff list, else nothing — in which case say so plainly.
  _renderWmj(a) {
    if (this._editingWmj === a.id) {
      return html`
        <div class="wmj">
          <span>Workamajig user</span>
          <input .value=${this._wmjKey} placeholder="Workamajig name, e.g. Donovan Buck"
            @input=${e => (this._wmjKey = e.target.value)} />
          <button class="primary" @click=${() => this._saveWmj(a)}>Save</button>
          <button @click=${() => (this._editingWmj = null)}>Cancel</button>
        </div>`
    }
    return html`
      <div class="wmj">
        ${a.resolvedWorkamajigUserId
          ? html`<span>Workamajig user <strong>${a.resolvedWorkamajigUserId}</strong>${a.workamajigUserId ? ' (set here)' : ' (matched by email)'}</span>`
          : html`<span class="missing">No Workamajig user — bookings for them will fail until this is set</span>`}
        <button @click=${() => this._openWmj(a)}>${a.workamajigUserId ? 'Change' : 'Set'}</button>
      </div>`
  }

  async _reactivate(agent) {
    try {
      await api.post(`/users/${agent.id}/reactivate`)
      toast(`Reactivated ${agent.fullName || agent.email}`, 'success')
      this._load()
    } catch (err) {
      toast(err.message || 'Failed to reactivate', 'error')
    }
  }

  render() {
    if (this._loading) return html`<div class="loading">Loading…</div>`
    const meId = store.get().user?.id
    return html`
      <h1>Agents</h1>
      <p class="lede">Suspend an agent to block sign-in and reassign their open tickets; reactivate to restore access.
        The Workamajig user key is who a ticket's task is assigned to on the job.</p>
      <div class="list">
        ${this._agents.map(a => html`
          <div class="row">
            <div class="head">
              <span class="name">${a.fullName || a.email}</span>
              <span class="groups">
                ${a.groups?.length
                  ? a.groups.map(g => html`<span class="group-tag">${g.name}</span>`)
                  : html`<span class="group-tag none">No groups</span>`}
              </span>
              <span class="badge ${a.status}">${a.status}</span>
              <span class="count">${a.openTicketCount} open</span>
              ${a.status === 'active'
                ? (a.id === meId ? html`<span class="count">(you)</span>`
                    : html`<button class="danger" @click=${() => this._openSuspend(a.id)}>Suspend</button>`)
                : html`<button @click=${() => this._reactivate(a)}>Reactivate</button>`}
            </div>
            ${this._renderWmj(a)}
            ${this._suspending === a.id ? html`
              <div class="reassign">
                <span class="count">Reassign ${a.openTicketCount} open ticket${a.openTicketCount === 1 ? '' : 's'} to:</span>
                <select @change=${e => (this._target = e.target.value)}>
                  <option value="">Unassign</option>
                  <optgroup label="Agent">
                    ${this._agents.filter(x => x.status === 'active' && x.id !== a.id)
                      .map(x => html`<option value=${'agent:' + x.id}>${x.fullName || x.email}</option>`)}
                  </optgroup>
                  <optgroup label="Group">
                    ${this._groups.map(g => html`<option value=${'group:' + g.id}>${g.name}</option>`)}
                  </optgroup>
                </select>
                <button class="primary" @click=${() => this._confirmSuspend(a)}>Confirm suspend</button>
                <button @click=${() => (this._suspending = null)}>Cancel</button>
              </div>` : ''}
          </div>`)}
      </div>
    `
  }
}

customElements.define('page-agents', PageAgents)
